import { QueryHookOptions, useQuery } from '@apollo/client';
import { pathEq } from 'ramda';

import {
  FeatureName,
  TeamMembersRequestStatus,
  TeamMembersRequestType,
} from '__generated__/GQL';
import mergeConnectionResultsBy from 'common/graphql/mergeConnectionResultsBy';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { ExpenseClaimRequestFragment } from 'features/Team/pages/Requests/graphql/ExpenseClaimRequestFragment.gql';
import { VirtualBudgetCardOrderRequestFragment } from 'features/Team/pages/Requests/graphql/VirtualBudgetCardOrderRequestFragment.gql';
import {
  TeamRequestsDocument,
  TeamRequestsQuery,
  TeamRequestsQueryVariables,
} from 'features/Team/pages/Requests/hooks/useRequests/teamRequests.gql';

import { BankTransferRequestFragment } from '../../graphql/BankTransferRequestFragment.gql';
import { PhysicalPaymentCardOrderRequestFragment } from '../../graphql/PhysicalPaymentCardOrderRequestFragment.gql';
import { VirtualPaymentCardOrderRequestFragment } from '../../graphql/VirtualPaymentCardOrderRequestFragment.gql';

export type PaymentCardRequest =
  | VirtualBudgetCardOrderRequestFragment
  | PhysicalPaymentCardOrderRequestFragment
  | VirtualPaymentCardOrderRequestFragment;

export type Request =
  | PaymentCardRequest
  | BankTransferRequestFragment
  | ExpenseClaimRequestFragment;

export type TeamRequestsQueryPageInfo =
  TeamRequestsQuery['viewer']['company']['teamMembersRequests']['pageInfo'];

export const isExpenseClaimRequest = (
  request: Request,
): request is ExpenseClaimRequestFragment => {
  return (
    request.__typename === 'ExpenseClaimRequest' &&
    request.type === TeamMembersRequestType.ExpenseClaim
  );
};

export const isBankTransferRequest = (
  request: Request,
): request is BankTransferRequestFragment => {
  return (
    request.__typename === 'BankTransferRequest' &&
    request.type === TeamMembersRequestType.BankTransfer
  );
};

export const isPaymentCardRequest = (
  request: Request,
): request is PaymentCardRequest => {
  return (
    (request.__typename === 'VirtualPaymentCardOrderRequest' ||
      request.__typename === 'PhysicalPaymentCardOrderRequest' ||
      request.__typename === 'VirtualBudgetCardOrderRequest') &&
    (request.type === TeamMembersRequestType.VirtualPaymentCardOrder ||
      request.type === TeamMembersRequestType.PhysicalPaymentCardOrder ||
      request.type === TeamMembersRequestType.VirtualBudgetCardOrder)
  );
};

export const isVirtualBudgetCardRequest = (
  request: Request,
): request is VirtualBudgetCardOrderRequestFragment => {
  return (
    request.__typename === 'VirtualBudgetCardOrderRequest' &&
    request.type === TeamMembersRequestType.VirtualBudgetCardOrder
  );
};

export const isPhysicalPaymentCardRequest = (
  request: Request,
): request is PhysicalPaymentCardOrderRequestFragment => {
  return (
    request.__typename === 'PhysicalPaymentCardOrderRequest' &&
    request.type === TeamMembersRequestType.PhysicalPaymentCardOrder
  );
};

export const useRequests = (
  status: TeamMembersRequestStatus[],
  options?: Omit<
    QueryHookOptions<TeamRequestsQuery, TeamRequestsQueryVariables>,
    'variables'
  >,
) => {
  const { companyProfileId } = useCompanyContext();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();

  const { data, fetchMore, previousData, ...hook } = useQuery(
    TeamRequestsDocument,
    {
      ...options,
      notifyOnNetworkStatusChange: true,
      skip:
        !canAccessFeature({
          name: FeatureName.TeamRequests,
          withChildFeatureOnly: true,
        }) ||
        !companyProfileId ||
        (options?.skip ?? false),
      variables: {
        after: null,
        companyProfileId: companyProfileId ?? '',
        status,
      },
    },
  );

  const loadMore = () => {
    return fetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const isEqual = pathEq(
          previousResult,
          ['viewer', 'company', 'teamMembersRequests', 'pageInfo'],
          fetchMoreResult,
        );
        if (isEqual) {
          return previousResult;
        }
        return mergeConnectionResultsBy('teamMembersRequestId')(
          previousResult,
          fetchMoreResult,
        );
      },
      variables: {
        after: data?.viewer.company.teamMembersRequests.pageInfo.nextCursor,
      },
    });
  };

  const requests =
    data?.viewer.company.teamMembersRequests.edges.map(
      (edge) => edge.node as Request,
    ) ?? [];

  const pageInfo =
    data?.viewer.company.teamMembersRequests.pageInfo ??
    ({
      __typename: 'TeamMembersRequestsPageInfo',
      hasNextPage: false,
      nextCursor:
        previousData?.viewer.company.teamMembersRequests.pageInfo.nextCursor ??
        null,
      totalCount:
        previousData?.viewer.company.teamMembersRequests.pageInfo.totalCount ??
        0,
    } satisfies TeamRequestsQueryPageInfo);

  return { loadMore, pageInfo, requests, ...hook } as const;
};

export { TeamRequestsDocument };
